import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useStore } from '@tanstack/react-form';
import { Fragment } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import formMessages from '~/components/form-messages';
import Spacings from '~/modules/ui-kit/spacings';
import messages from './messages';
import styles from './styles.module.css';
import { useAppForm } from '../Form';

const schema = (intl: IntlShape) =>
  z.object({
    name: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    meetingPlace: z.string().nullable(),
    assignmentRange: z
      .object({
        minPublishers: z
          .number({
            error: intl.formatMessage(formMessages.required),
          })
          .min(
            1,
            intl.formatMessage(messages.minPublishersMustBeGreaterThanZero)
          ),
        maxPublishers: z
          .number({
            error: intl.formatMessage(formMessages.required),
          })
          .min(
            1,
            intl.formatMessage(messages.maxPublishersMustBeGreaterThanZero)
          ),
      })
      .refine(
        (data) => data.maxPublishers >= data.minPublishers,
        intl.formatMessage(messages.maxPublishersMustBeGreaterThanMinPublishers)
      ),
    brotherRequirement: z
      .array(
        z.object({
          threshold: z.number(),
          brotherCount: z.number(),
        })
      )
      .superRefine((bands, ctx) => {
        if (bands.length === 0) {
          return;
        }
        bands.forEach((band, index) => {
          if (band.threshold < 1 || band.brotherCount < 1) {
            ctx.addIssue({
              code: 'custom',
              path: [index, 'brotherCount'],
              message: intl.formatMessage(
                messages.brotherRequirementCountsMustBePositive
              ),
            });
          }
        });
        if (bands[0].threshold !== 1) {
          ctx.addIssue({
            code: 'custom',
            path: [0, 'threshold'],
            message: intl.formatMessage(
              messages.brotherRequirementFirstBandMustStartAtOne
            ),
          });
        }
        for (let index = 1; index < bands.length; index++) {
          if (bands[index].threshold <= bands[index - 1].threshold) {
            ctx.addIssue({
              code: 'custom',
              path: [index, 'threshold'],
              message: intl.formatMessage(
                messages.brotherRequirementThresholdsMustIncrease
              ),
            });
          }
          if (bands[index].brotherCount <= bands[index - 1].brotherCount) {
            ctx.addIssue({
              code: 'custom',
              path: [index, 'brotherCount'],
              message: intl.formatMessage(
                messages.brotherRequirementBrotherCountsMustIncrease
              ),
            });
          }
        }
      }),
    enableAssignmentRangeFirstShift: z.boolean(),
    assignmentRangeFirstShift: z
      .object({
        minPublishers: z.number(),
        maxPublishers: z.number(),
      })
      .nullable(),
    enableAssignmentRangeLastShift: z.boolean(),
    assignmentRangeLastShift: z
      .object({
        minPublishers: z.number(),
        maxPublishers: z.number(),
      })
      .nullable(),
    depot: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    evenNumberOfParticipants: z.boolean(),
    keepRouteStaffed: z.boolean(),
  });

type FormValues = z.infer<ReturnType<typeof schema>>;

export type RouteFormSubmitValues = Omit<FormValues, 'depot'> & {
  depot: number;
};

type RouteFormProps = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: RouteFormSubmitValues) => void;
  availableDepots: Array<{
    id: number;
    name?: string;
  }>;
};

const RouteForm = ({
  initialValues,
  onSubmit,
  availableDepots,
}: RouteFormProps) => {
  const intl = useIntl();

  const form = useAppForm({
    defaultValues: {
      name: '',
      meetingPlace: '',
      assignmentRange: {
        minPublishers: 1,
        maxPublishers: 1,
      },
      brotherRequirement: [],
      assignmentRangeFirstShift: {
        minPublishers: 1,
        maxPublishers: 1,
      },
      assignmentRangeLastShift: {
        minPublishers: 1,
        maxPublishers: 1,
      },
      depot: '',
      evenNumberOfParticipants: false,
      keepRouteStaffed: false,
      enableAssignmentRangeFirstShift: Boolean(
        initialValues?.assignmentRangeFirstShift
      ),
      enableAssignmentRangeLastShift: Boolean(
        initialValues?.assignmentRangeLastShift
      ),
      ...initialValues,
    },
    validators: {
      onChange: schema(intl),
    },
    onSubmit: ({ value }) => {
      onSubmit({
        ...value,
        depot: parseInt(value.depot, 10),
      });
    },
  });

  const enableAssignmentRangeFirstShift = useStore(
    form.store,
    (state) => state.values.enableAssignmentRangeFirstShift
  );
  const enableAssignmentRangeLastShift = useStore(
    form.store,
    (state) => state.values.enableAssignmentRangeLastShift
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className={styles.form}
    >
      <Spacings.Stack scale="xl">
        <Spacings.Stack scale="m">
          <Typography variant="h6">
            <FormattedMessage {...messages.commonSettings} />
          </Typography>

          <form.AppField name="name">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.name} />}
              />
            )}
          </form.AppField>

          <form.AppField name="meetingPlace">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.meetingPlace} />}
              />
            )}
          </form.AppField>

          <form.AppField name="assignmentRange.minPublishers">
            {(field) => (
              <field.NumberField
                label={<FormattedMessage {...messages.minPublishers} />}
              />
            )}
          </form.AppField>

          <form.AppField name="assignmentRange.maxPublishers">
            {(field) => (
              <field.NumberField
                label={<FormattedMessage {...messages.maxPublishers} />}
              />
            )}
          </form.AppField>

          <Spacings.Stack scale="s">
            <Typography variant="subtitle2">
              <FormattedMessage {...messages.brotherRequirement} />
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage {...messages.brotherRequirementDescription} />
            </Typography>

            <form.Field name="brotherRequirement" mode="array">
              {(arrayField) => (
                <Spacings.Stack scale="s">
                  {arrayField.state.value.length > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '12rem 1fr auto',
                        columnGap: 2,
                        rowGap: 1,
                        alignItems: 'start',
                      }}
                    >
                      {arrayField.state.value.map((_, index) => (
                        <Fragment key={index}>
                          {index === 0 ? (
                            <Typography sx={{ pt: 2 }}>
                              <FormattedMessage
                                {...messages.brotherRequirementFromFirstParticipant}
                              />
                            </Typography>
                          ) : (
                            <form.AppField
                              name={`brotherRequirement[${index}].threshold`}
                            >
                              {(field) => (
                                <field.NumberField
                                  label={
                                    <FormattedMessage
                                      {...messages.brotherRequirementFromParticipants}
                                    />
                                  }
                                />
                              )}
                            </form.AppField>
                          )}
                          <form.AppField
                            name={`brotherRequirement[${index}].brotherCount`}
                          >
                            {(field) => (
                              <field.NumberField
                                label={
                                  <FormattedMessage
                                    {...messages.brotherRequirementBrothers}
                                  />
                                }
                              />
                            )}
                          </form.AppField>
                          <IconButton
                            aria-label={intl.formatMessage(
                              messages.brotherRequirementRemoveBand
                            )}
                            onClick={() => {
                              const hadMoreBands =
                                arrayField.state.value.length > 1;
                              arrayField.removeValue(index);
                              // The first band always starts at one participant, so
                              // keep a newly-promoted first band consistent with the
                              // implicit label rather than leaving an unfixable value.
                              if (index === 0 && hadMoreBands) {
                                form.setFieldValue(
                                  'brotherRequirement[0].threshold',
                                  1
                                );
                              }
                            }}
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Fragment>
                      ))}
                    </Box>
                  ) : null}
                  <Box>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const bands = arrayField.state.value;
                        const last = bands[bands.length - 1];
                        arrayField.pushValue({
                          threshold: last ? last.threshold + 1 : 1,
                          brotherCount: last ? last.brotherCount + 1 : 1,
                        });
                      }}
                    >
                      <FormattedMessage
                        {...messages.brotherRequirementAddBand}
                      />
                    </Button>
                  </Box>
                </Spacings.Stack>
              )}
            </form.Field>
          </Spacings.Stack>

          <Spacings.Stack scale="m" className={styles.specialAssignmentRanges}>
            <Spacings.Stack>
              <Typography variant="subtitle2">
                <FormattedMessage {...messages.assignmentRangeFirstShift} />
              </Typography>

              <form.AppField name="enableAssignmentRangeFirstShift">
                {(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.state.value}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            field.form.setFieldValue(
                              'assignmentRangeFirstShift',
                              null
                            );
                          }
                          return field.handleChange(e.target.checked);
                        }}
                        onBlur={field.handleBlur}
                        color="primary"
                      />
                    }
                    label={
                      <FormattedMessage
                        {...messages.enableAssignmentRangeFirstShift}
                      />
                    }
                  />
                )}
              </form.AppField>

              {enableAssignmentRangeFirstShift ? (
                <Spacings.Stack scale="m">
                  <form.AppField name="assignmentRangeFirstShift.minPublishers">
                    {(field) => (
                      <field.NumberField
                        label={<FormattedMessage {...messages.minPublishers} />}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="assignmentRangeFirstShift.maxPublishers">
                    {(field) => (
                      <field.NumberField
                        label={<FormattedMessage {...messages.maxPublishers} />}
                      />
                    )}
                  </form.AppField>
                </Spacings.Stack>
              ) : null}
            </Spacings.Stack>

            <Spacings.Stack>
              <Typography variant="subtitle2">
                <FormattedMessage {...messages.assignmentRangeLastShift} />
              </Typography>

              <form.AppField name="enableAssignmentRangeLastShift">
                {(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.state.value}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            field.form.setFieldValue(
                              'assignmentRangeLastShift',
                              null
                            );
                          }
                          return field.handleChange(e.target.checked);
                        }}
                        onBlur={field.handleBlur}
                        color="primary"
                      />
                    }
                    label={
                      <FormattedMessage
                        {...messages.enableAssignmentRangeLastShift}
                      />
                    }
                  />
                )}
              </form.AppField>

              {enableAssignmentRangeLastShift && (
                <Spacings.Stack scale="m">
                  <form.AppField name="assignmentRangeLastShift.minPublishers">
                    {(field) => (
                      <field.NumberField
                        label={<FormattedMessage {...messages.minPublishers} />}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="assignmentRangeLastShift.maxPublishers">
                    {(field) => (
                      <field.NumberField
                        label={<FormattedMessage {...messages.maxPublishers} />}
                      />
                    )}
                  </form.AppField>
                </Spacings.Stack>
              )}
            </Spacings.Stack>
          </Spacings.Stack>

          <form.AppField name="depot">
            {(field) => (
              <field.SelectField
                label={<FormattedMessage {...messages.depot} />}
                options={availableDepots.map((depot) => ({
                  value: depot.id.toString(),
                  label: depot.name,
                }))}
              />
            )}
          </form.AppField>
        </Spacings.Stack>

        <Spacings.Stack>
          <Typography variant="h6">
            <FormattedMessage {...messages.settingsForAutomatedPlanning} />
          </Typography>

          <form.AppField name="evenNumberOfParticipants">
            {(field) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    onBlur={field.handleBlur}
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage {...messages.evenNumberOfParticipants} />
                }
              />
            )}
          </form.AppField>

          <form.AppField name="keepRouteStaffed">
            {(field) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    onBlur={field.handleBlur}
                    color="primary"
                  />
                }
                label={<FormattedMessage {...messages.keepRouteStaffed} />}
              />
            )}
          </form.AppField>

          <Typography
            className={styles['checkbox-inset']}
            variant="body2"
            color="textSecondary"
          >
            <FormattedMessage {...messages.keepRouteStaffedDescription} />
          </Typography>
        </Spacings.Stack>

        <div>
          <form.AppForm>
            <form.SubmitButton>
              <FormattedMessage {...formMessages.save} />
            </form.SubmitButton>
          </form.AppForm>
        </div>
      </Spacings.Stack>
    </form>
  );
};

export default RouteForm;
